import React from 'react'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { XMarkIcon, Bars3Icon } from '@heroicons/react/24/outline'
import { Activity } from '../../types'
import { getDisplayDurationMinutes } from '../../utils/activityConfiguration'
import { INTERAX_COLORS, INTERAX_TYPOGRAPHY } from '../../../../../constants/colors'
import { TESTIDS } from '../../../../../config/testids'

export interface SortableActivityCardProps {
  activity: Activity
  onRemove?: (id: string) => void
  isSelected?: boolean
  onSelect?: (id: string) => void
  isDraggable?: boolean
  details?: React.ReactNode
  /** Optional control rendered inline next to the duration (e.g. an edit toggle). */
  durationEditSlot?: React.ReactNode
}

export const SortableActivityCard: React.FC<SortableActivityCardProps> = ({
  activity,
  onRemove,
  isSelected = false,
  onSelect,
  isDraggable = true,
  details,
  durationEditSlot
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: activity.id })
  const displayDuration = getDisplayDurationMinutes(activity)

  return (
    <div className="w-full flex flex-row">
      <div
        ref={setNodeRef}
        className={`flex-1 p-4 rounded-lg ${isDraggable ? 'cursor-move' : 'cursor-default'} ${isSelected ? 'ring-2 ring-sun' : ''}`}
        style={{
          backgroundColor: INTERAX_COLORS.heritageBlue40,
          borderColor: isSelected ? INTERAX_COLORS.sun : INTERAX_COLORS.heritageBlue,
          borderWidth: '1px',
          borderStyle: 'solid',
          borderRightWidth: onRemove ? '0px' : '1px', // Remove right border to connect with X button
          transform: CSS.Transform.toString(transform),
          transition,
          opacity: isDragging ? 0.5 : 1
        }}
        onClick={() => onSelect?.(activity.id)}
      >
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <div
              className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
              style={{ backgroundColor: INTERAX_COLORS.sun40 }}
            >
              <div style={{ color: INTERAX_COLORS.sun }}>
                <activity.icon className="w-5 h-5" />
              </div>
            </div>

            <div className="flex-1">
              <h4
                className="font-semibold"
                style={{
                  color: INTERAX_COLORS.white,
                  fontFamily: INTERAX_TYPOGRAPHY.headline4.fontFamily,
                  fontWeight: INTERAX_TYPOGRAPHY.headline4.fontWeight,
                  fontSize: INTERAX_TYPOGRAPHY.headline4.fontSize,
                  lineHeight: INTERAX_TYPOGRAPHY.headline4.lineHeight
                }}
              >
                {activity.title}
              </h4>
              <div
                className="flex items-center gap-2 text-sm"
                style={{
                  color: INTERAX_COLORS.sun,
                  fontFamily: INTERAX_TYPOGRAPHY.body3.fontFamily,
                  fontWeight: INTERAX_TYPOGRAPHY.body3.fontWeight,
                  fontSize: INTERAX_TYPOGRAPHY.body3.fontSize,
                  lineHeight: INTERAX_TYPOGRAPHY.body3.lineHeight
                }}
              >
                <span>⏱</span>
                <span>{displayDuration} min</span>
                {durationEditSlot}
              </div>
            </div>

            {isDraggable && (
              <div
                className="flex items-center justify-center p-1 cursor-move self-center"
                {...attributes}
                {...listeners}
                onClick={(event) => event.stopPropagation()}
              >
                <Bars3Icon className="w-4 h-4" style={{ color: INTERAX_COLORS.thunderGrey }} />
              </div>
            )}
          </div>

          {details && <div className="mt-3 ml-11">{details}</div>}
        </div>
      </div>

      {/* X button - only show if onRemove is provided */}
      {onRemove && (
        <button
          data-testid={`${TESTIDS.SPRINT_ACTIVITY_REMOVE_BTN}-${activity.id}`}
          aria-label="Remove activity"
          onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()

            onRemove(activity.id)
          }}
          className="flex items-center justify-center px-2 rounded-r-lg transition-colors hover:opacity-80"
          style={{
            backgroundColor: INTERAX_COLORS.redErrorBackground,
            color: INTERAX_COLORS.redError,
            borderColor: isSelected ? INTERAX_COLORS.sun : INTERAX_COLORS.redErrorBackground,
            borderWidth: '1px',
            borderStyle: 'solid',
            borderLeftWidth: '0px' // Remove left border to connect with main content
          }}
        >
          <XMarkIcon className="w-4 h-4" />
        </button>
      )}
    </div>
  )
}
